import { Query } from "@apollo/client/react/components";
import cn from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useState, useRef, useEffect } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";

import { GET_ALL_PAGES_USER } from "@api/queriesV2/cms";
import BlockTitle from "@components/cms/BlockTitle/BlockTitle";
import { getPageFolderName, getPageTypes } from "@config/cms";
import { useStore } from "@hooks/useZustandProvider";
import i from "@styles/icons.module.scss";
import Editor from "@ui-kit/cms/Editor/Editor";
import Container from "@ui-kit/Container/Container";
import Loading from "@ui-kit/Loading/Loading";
import { addBlankAttrToHtml, localizedDate } from "@utils/utils";

import s from "./NewsBlock.module.scss";

const NewsList = ({ newsList, idNews }) => {
  const { locale } = useRouter();
  const { t } = useTranslation(["common"]);
  const filteredNews = newsList
    .filter((news) => idNews != news.id)
    .sort(
      (a, b) =>
        new Date(b.properties.datePublished) -
        new Date(a.properties.datePublished),
    );

  return (
    !!filteredNews.length && (
      <aside>
        <h3>{t("more news")}</h3>
        <div className={s.news}>
          {filteredNews.slice(0, 3).map((news, key) => {
            const slug = news.data.slug;
            const date = news.properties.datePublished;
            const title = news.data.seo.title;
            const description = news.data.seo.description;
            return (
              <Link
                className={s.item}
                key={key}
                href={`/${getPageFolderName().news}/${slug}`}
              >
                <div className={s.date}>
                  {localizedDate(date, "dd MMMM yyyy", locale)}
                </div>
                <h5>{title}</h5>
                <p>{description}</p>
              </Link>
            );
          })}
        </div>
        {/* <Link href="/blog" className={s.showLatest}>
          {t("show latest news")}
          <icon className={i.arrowRight_v2}/>
        </Link> */}
      </aside>
    )
  );
};

const NewsBlock = ({ settings, data, updateField }) => {
  const { t } = useTranslation();
  const { id, title, news } = data;
  const { locale } = useRouter();
  const { cms } = useStore();
  const { isEditMode } = cms.uiOptions;
  const { lang } = cms.page;
  const { blockType } = settings.common;

  const facebookShareButton = useRef(null);
  const twitterShareButton = useRef(null);

  const [copyLink, setCopyLink] = useState({
    link: "",
    show: false,
  });

  const onCopy = () => {
    setCopyLink((prev) => ({ ...prev, show: true }));
  };

  useEffect(() => {
    if (!isEditMode) {
      if (facebookShareButton.current) {
        facebookShareButton.current.addEventListener("click", function () {
          const url = window.location.href;
          window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${url}`,
            "facebook",
            "width=800,height=600",
          );
          return false;
        });
      }

      if (twitterShareButton.current) {
        twitterShareButton.current.addEventListener("click", function () {
          const hashTags = `luxexpres,estonia`;
          const url = window.location.href;
          const text = "Lux Express - the most comfortable seat on highways";

          window.open(
            // `https://twitter.com/share?url=${url}&text=${text}&hashtags=${hashTags}`,
            `https://twitter.com/share?url=${url}`,
            "twitter",
            "width=800,height=600",
          );
          return false;
        });
      }
    }
  }, []);

  return (
    <Container className={s.news}>
      <Container full={false}>
        <section>
          <BlockTitle
            settings={settings}
            field={{ title }}
            onChange={updateField}
            type={blockType}
          />
          <article>
            <div className={s.texts}>
              <Editor
                field={{ news: addBlankAttrToHtml(news) }}
                onChange={updateField}
              />
              <div className={s.links}>
                <div
                  ref={facebookShareButton}
                  className={s.link}
                  id="fb-share-button"
                  data-href={copyLink.link}
                >
                  <icon className={i.facebook} />
                </div>
                <CopyToClipboard onCopy={onCopy} text={copyLink.link}>
                  <div className={s.link} onClick={copyLink}>
                    <icon className={i.link} />
                  </div>
                </CopyToClipboard>
                <div
                  ref={twitterShareButton}
                  className={s.link}
                  id="twitter-share-button"
                >
                  <icon className={i.twitter} />
                </div>
                <div
                  onAnimationEnd={() =>
                    setCopyLink((prev) => ({ ...prev, show: false }))
                  }
                  className={cn(s.copied, copyLink.show && s.animate)}
                >
                  {t("copied")}
                </div>
              </div>
            </div>
            <Query
              query={GET_ALL_PAGES_USER}
              context={{ clientName: "v2" }}
              variables={{
                language: isEditMode ? lang : locale,
                type: getPageTypes().news,
              }}
            >
              {({ data, loading, errors }) => {
                if (loading) return <Loading />;

                if (errors) {
                  return (
                    <div className={s.newsList}>
                      Ops something wend wrong :(
                    </div>
                  );
                }

                const newsList = data.getAllPagesUser;

                return <NewsList t={t} idNews={id} newsList={newsList} />;
              }}
            </Query>
          </article>
        </section>
      </Container>
    </Container>
  );
};

export default NewsBlock;
